import { Suspense, useState } from "react";
import { MoreVert, Tune, RemoveCircleOutline } from "@mui/icons-material";
import {
  CircularProgress,
  Collapse,
  IconButton,
  alpha,
  ListItem,
  ListItemBaseProps,
  ListItemText,
  Menu,
  MenuItem,
  styled,
  Switch,
  Typography,
  useTheme,
  Skeleton,
  Stack,
  Tooltip,
  ListItemIcon,
} from "@mui/material";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import {
  Draggable,
  DraggingStyle,
  NotDraggingStyle,
} from "react-beautiful-dnd";

import {
  LayerProperties,
  Selection,
  IndicatorSelection,
  Indicator,
} from "../../types";
import { LayerItemDetails } from "./LayerItemDetails";
import {
  useHasSelectedLayer,
  useSelection,
  useUpdateSelectionMutation,
} from "hooks/selectionHooks";
import { useIndicator } from "hooks/indicatorHooks";
import {
  removeIndicatorFromSelection,
  updateSelectionIndicators,
} from "utils/IndicatorSelectionUtils";

interface Props extends ListItemBaseProps {
  config: string;
  selection: IndicatorSelection;
  layerProperties: LayerProperties[];
  order: number;
}

export function LayerItem(props: Props) {
  const {
    config,
    layerProperties,
    selection: singleSelection,
    ...itemProps
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const { data: indicator } = useIndicator(
    config,
    singleSelection.indicatorId,
    layerProperties
  );
  const { data: selection } = useSelection(config, layerProperties);
  const updateSelectionMutation = useUpdateSelectionMutation(
    config,
    layerProperties
  );
  const { data: hasSelectedLayer, isFetched } = useHasSelectedLayer(
    config,
    indicator,
    layerProperties
  );
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [optionsRef, setOptionsRef] = useState<HTMLElement | null>(null);

  function getItemStyle(
    isDragging: boolean,
    draggableStyle: DraggingStyle | NotDraggingStyle | undefined
  ) {
    return {
      background: isDragging ? alpha(theme.palette.grey[100], 0.2) : "unset",
      ...draggableStyle,
    };
  }

  function handleToggleOpacity(
    selection: Selection,
    update: IndicatorSelection
  ) {
    const updatedSelection = updateSelectionIndicators(selection, update);
    updateSelectionMutation.mutateAsync(updatedSelection);
  }

  function handleToggleActive(
    selection: Selection,
    update: IndicatorSelection
  ) {
    const updatedSelection = updateSelectionIndicators(selection, {
      ...update,
      active: !update.active,
    });

    updateSelectionMutation.mutateAsync(updatedSelection);
  }

  async function handleRemoveToSelection(
    selection: Selection,
    indicator: Indicator
  ) {
    const updatedSelection = removeIndicatorFromSelection(selection, indicator);
    await updateSelectionMutation.mutateAsync(updatedSelection);
  }

  return render();

  function render() {
    if (!selection) return null;
    if (!indicator) return null;

    return (
      <Draggable
        draggableId={singleSelection.indicatorId.toString()}
        index={props.order}
      >
        {(provided, snapshot) => (
          <>
            <StyledListItem
              {...itemProps}
              {...provided.draggableProps}
              style={getItemStyle(
                snapshot.isDragging,
                provided.draggableProps.style
              )}
              ref={provided.innerRef}
              secondaryAction={
                <>
                  <IconButton size="small" onClick={() => setOpen(!open)}>
                    <Tune color={open ? "primary" : undefined} />
                  </IconButton>
                  <Switch
                    edge="end"
                    checked={singleSelection.active}
                    onClick={() =>
                      handleToggleActive(selection, singleSelection)
                    }
                  />
                  <IconButton
                    size="small"
                    onClick={() => setOptionsOpen(!optionsOpen)}
                    ref={(ref) => setOptionsRef(ref)}
                  >
                    <MoreVert color={optionsOpen ? "primary" : undefined} />
                  </IconButton>
                  <Menu
                    open={optionsOpen}
                    anchorEl={optionsRef}
                    onClose={() => setOptionsOpen(!optionsOpen)}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  >
                    <MenuItem
                      onClick={async () => {
                        setIsRemoving(true);
                        try {
                          await handleRemoveToSelection(selection, indicator);
                          setOptionsOpen(!optionsOpen);
                        } catch (err) {}
                        setIsRemoving(false);
                      }}
                      disabled={isRemoving}
                    >
                      {t("label.remove")}
                      {isRemoving && (
                        <CircularProgress
                          size="small"
                          sx={{
                            position: "absolute",
                            width: "20px",
                            margin: "0 35px",
                          }}
                        />
                      )}
                    </MenuItem>
                  </Menu>
                </>
              }
            >
              {!hasSelectedLayer && isFetched && (
                <ListItemIcon>
                  <RemoveCircleOutline fontSize="small" color="error" />
                </ListItemIcon>
              )}
              <Tooltip
                title={
                  !hasSelectedLayer && isFetched
                    ? t("label.noActiveLayerForSelection").toString()
                    : ""
                }
                placement="right"
              >
                <ListItemText
                  primary={`${indicator.title}`}
                  className={
                    !hasSelectedLayer && isFetched
                      ? "disabled-state"
                      : "enabled-state"
                  }
                  primaryTypographyProps={{
                    noWrap: true,
                    component: "p",
                    maxWidth: "138px",
                    title: indicator.title,
                  }}
                  {...provided.dragHandleProps}
                />
              </Tooltip>
            </StyledListItem>
            <Collapse in={open} unmountOnExit>
              <ListItemColumn>
                <ErrorBoundary
                  fallbackRender={() => (
                    <Typography color="error">
                      {t("label.unableToLoadDetails")}
                    </Typography>
                  )}
                >
                  <Suspense
                    fallback={
                      <Stack direction="column">
                        <Skeleton variant="text-invert" width="100%" />
                        <Skeleton variant="text-invert" width="100%" />
                        <Skeleton variant="text-invert" width="100%" />
                      </Stack>
                    }
                  >
                    <LayerItemDetails
                      indicator={indicator}
                      selection={props.selection}
                      config={config}
                      layerProperties={layerProperties}
                      onOpacityChange={(opacity) => {
                        handleToggleOpacity(selection, {
                          ...singleSelection,
                          opacity,
                        });
                      }}
                    />
                  </Suspense>
                </ErrorBoundary>
              </ListItemColumn>
            </Collapse>
          </>
        )}
      </Draggable>
    );
  }
}

const StyledListItem = styled(ListItem)`
  &.MuiListItem-root {
    display: flex;
    padding: ${({ theme }) => theme.spacing(0)};

    .disabled-state {
      .MuiTypography-root {
        color: ${({ theme }) => alpha(theme.palette.grey[200], 0.5)};
      }
    }

    .MuiListItemText-root {
      flex: 1 1 auto;
      padding: ${({ theme }) => theme.spacing(0, 0.5)};
    }

    .MuiListItemSecondaryAction-root {
      position: unset;
      top: unset;
      right: unset;
      transform: unset;
      flex: 0 1 auto;
      display: flex;
      flex-wrap: nowrap;
      justify-content: center;
      align-items: center;
    }

    .MuiListItemIcon-root {
      min-width: unset;
      margin-right: ${({ theme }) => theme.spacing(0.5)};
    }
  }
`;

const ListItemColumn = styled(ListItem)`
  background-color: ${({ theme }) => theme.palette.background.default};
  flex-direction: column;
  align-items: stretch;
`;
